<template>
  <div class="beta-banner-wrapper" v-if="showBanner">
    <div class="beta-banner" ref="bannerRef">
      <div class="banner-content">
        <div class="banner-text">
          <span class="font-weight-bold">BETA:</span>
          <span class="ml-1">We're actively refining our IS Code calculators. Help us improve!</span>
        </div>

        <div class="banner-actions">
          <se-button
            variant="secondary"
            style-set="set8"
            class="feedback-btn"
            @click="$emit('open-feedback')"
          >
            Give Feedback
          </se-button>

          <se-button
            variant="text"
            style-set="set8"
            class="close-btn"
            @click="closeBanner"
            aria-label="Dismiss banner"
          >
            <v-icon icon="mdi-close" size="small" />
          </se-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import SeButton from '@/components/SEButton.vue'

defineEmits<{
  (e: 'open-feedback'): void
}>()

const showBanner = ref(false)
const bannerRef = ref<HTMLElement | null>(null)
let resizeObserver: ResizeObserver | null = null

const checkDismissal = () => {
  const dismissed = localStorage.getItem('beta_banner_dismissed')
  if (!dismissed) {
    showBanner.value = true
  }
}

const closeBanner = () => {
  showBanner.value = false
  localStorage.setItem('beta_banner_dismissed', 'true')
}

const getBannerEl = () => {
  // Try ref first, then fallback to DOM query
  return bannerRef.value ?? document.querySelector<HTMLElement>('.beta-banner')
}

const updateHeight = () => {
  const el = getBannerEl()
  if (el) {
    const height = el.getBoundingClientRect().height
    if (height > 0) {
      document.documentElement.style.setProperty('--beta-banner-height', `${height}px`)
    }
  }
}

// Watch visibility
watch(showBanner, async (visible) => {
  if (visible) {
    document.body.classList.add('has-beta-banner')

    // Wait for DOM update
    await nextTick()
    // Additional small delay to ensure Vuetify layout settles
    setTimeout(() => {
        const el = getBannerEl()
        if (el) {
          updateHeight()
          resizeObserver = new ResizeObserver(updateHeight)
          resizeObserver.observe(el)
        }
    }, 50)
  } else {
    document.body.classList.remove('has-beta-banner')
    document.documentElement.style.removeProperty('--beta-banner-height')

    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
  }
})

onMounted(() => {
  checkDismissal()
  // Add listener for storage events to sync across tabs
  window.addEventListener('storage', (e) => {
    if (e.key === 'beta_banner_dismissed' && e.newValue) {
      showBanner.value = false
    }
  })
})

onUnmounted(() => {
  document.body.classList.remove('has-beta-banner')
  document.documentElement.style.removeProperty('--beta-banner-height')
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<style scoped>
.beta-banner {
  background-color: var(--accent) !important;
  color: var(--on-accent) !important;
  padding: 8px 16px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: var(--z-banner); /* Higher than the sticky navigation tier */
  box-shadow: var(--shadow-sm);
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  flex-wrap: wrap;
  gap: 12px;
}

.banner-text {
  display: flex;
  align-items: center;
  font-size: var(--font-size-sm);
  text-align: center;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.feedback-btn {
  color: inherit !important;
  border-color: currentColor !important;
  text-transform: none;
  font-weight: 600;
}

.close-btn {
  color: inherit !important;
  margin-left: 8px;
  inline-size: 2rem;
  block-size: 2rem;
  padding: 0 !important;
  flex: 0 0 auto;
}

.close-btn :deep(.v-icon),
.close-btn :deep(.v-icon__svg) {
  inline-size: 1.25rem !important;
  block-size: 1.25rem !important;
}

/* Mobile responsiveness */
@media (max-width: 639px) {
  .banner-content {
    justify-content: space-between;
  }

  .banner-text {
    width: 100%;
    justify-content: center;
    margin-bottom: 4px;
    font-size: var(--font-size-caption);
  }

  .banner-actions {
    width: 100%;
    justify-content: center;
  }

  .close-btn {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    margin: 0;
  }

  /* If stacked, close button might need better positioning */
  .beta-banner {
    padding-right: 40px; /* Make room for close button */
  }
}
</style>

<style>
/* Global styles to push fixed header and body down */
body.has-beta-banner .app-header {
  top: var(--beta-banner-height, 40px) !important;
  transition: top 0.2s ease;
}

/*
  We need to push the content down as well.
*/
body.has-beta-banner {
  padding-top: var(--beta-banner-height, 40px) !important;
}


</style>
